import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ShoppingBag, Heart, Eye } from "lucide-react";
import { useCart } from "@/lib/cartContext";
import { useWishlist } from "@/lib/wishlistContext";
import { useToast } from "@/lib/toastContext";
import { useQuickView } from "@/lib/quickViewContext";
import type { Product } from "@/data/products";
import { cn } from "@/lib/utils";

interface ProductCardProps {
  product: Product;
}

const badgeStyle: Record<string, string> = {
  SALE: "bg-red-600",
  NEW: "bg-stone-900",
  "BEST SELLER": "bg-emerald-600",
};

export function ProductCard({ product }: ProductCardProps) {
  const { addItem } = useCart();
  const { toggle, has } = useWishlist();
  const { showToast } = useToast();
  const { openQuickView } = useQuickView();
  const [added, setAdded] = useState(false);
  const wished = has(product.id);

  const hasSizes = product.sizes && product.sizes.length > 0;

  function handleAdd(e: React.MouseEvent) {
    e.preventDefault();
    if (hasSizes) {
      openQuickView(product);
      return;
    }
    addItem(product);
    showToast(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  }

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  return (
    <NavLink to={`/product/${product.id}`} className="group block">
      {/* Image — portrait 3:4, contain con padding così ogni soggetto è centrato uguale */}
      <div className="relative overflow-hidden bg-white aspect-[3/4] isolate">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          draggable={false}
          className="absolute inset-0 w-full h-full object-contain object-center p-1.5 sm:p-4 md:p-5 select-none transform-gpu transition-transform duration-[600ms] ease-out group-hover:scale-[1.04]"
        />

        {/* Sconto % — in alto a sinistra */}
        {discount && (
          <span className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-600 text-white text-[9px] sm:text-xs font-bold px-1.5 sm:px-2.5 py-0.5 sm:py-1 leading-none z-10">
            −{discount}%
          </span>
        )}
        {/* NEW / BEST SELLER badge — sotto lo sconto a sinistra (no collisioni con wishlist a destra) */}
        {product.badge && product.badge !== "SALE" && (
          <span className={cn(
            "absolute left-2 sm:left-3 text-white text-[9px] sm:text-[10px] font-semibold px-1.5 sm:px-2 py-0.5 sm:py-1 tracking-widest uppercase leading-none z-10",
            discount ? "top-7 sm:top-10" : "top-2 sm:top-3",
            badgeStyle[product.badge]
          )}>
            {product.badge === "BEST SELLER" ? "Best" : product.badge}
          </span>
        )}

        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/10 transition-colors duration-300" />

        {/* Hover actions — slide up. Mobile: solo icone; sm+: icona + testo */}
        <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 flex">
          {/* Vista rapida */}
          <button
            onClick={(e) => { e.preventDefault(); openQuickView(product); }}
            aria-label="Vista rapida"
            className="flex-1 py-2.5 sm:py-3 text-[10px] sm:text-xs tracking-widest uppercase font-medium bg-white/95 text-stone-700 hover:bg-white hover:text-stone-900 transition-colors flex items-center justify-center gap-1.5 border-r border-stone-100"
          >
            <Eye className="size-3.5" />
            <span className="hidden sm:inline">Vista rapida</span>
          </button>

          {/* Aggiungi */}
          <button
            onClick={handleAdd}
            aria-label={hasSizes ? "Scegli taglia" : "Aggiungi al carrello"}
            className={cn(
              "flex-1 py-2.5 sm:py-3 text-[10px] sm:text-xs tracking-widest uppercase font-semibold transition-colors flex items-center justify-center gap-1.5",
              added ? "bg-emerald-600 text-white" : "bg-stone-900 text-white hover:bg-stone-700"
            )}
          >
            <ShoppingBag className="size-3.5" />
            <span className="hidden sm:inline">{added ? "Aggiunto" : hasSizes ? "Scegli" : "Aggiungi"}</span>
          </button>
        </div>

        {/* Wishlist */}
        <button
          onClick={(e) => { e.preventDefault(); toggle(product.id); }}
          aria-label="Aggiungi ai preferiti"
          className={cn(
            "absolute top-2 right-2 sm:top-3 sm:right-3 size-7 sm:size-8 rounded-full flex items-center justify-center transition-all",
            wished
              ? "bg-white opacity-100"
              : "bg-white/80 backdrop-blur-sm opacity-100 sm:opacity-0 sm:group-hover:opacity-100"
          )}
        >
          <Heart className={cn("size-3 sm:size-3.5", wished ? "fill-red-500 text-red-500" : "text-stone-400")} />
        </button>
      </div>

      {/* Info */}
      <div className="pt-2 pb-1">
        <p className="hidden sm:block text-[10px] tracking-widest uppercase text-stone-400 mb-0.5">{product.brand || product.category}</p>
        <h3 className="text-[11px] sm:text-sm font-medium text-stone-900 leading-snug mb-1 line-clamp-1 sm:line-clamp-2">{product.name}</h3>

        {/* Taglie disponibili */}
        {product.sizes && product.sizes.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1.5">
            {product.sizes.slice(0, 5).map((s) => (
              <span
                key={s}
                className="text-[9px] sm:text-[10px] tracking-wide text-stone-500 border border-stone-200 px-1 sm:px-1.5 py-0.5 leading-none"
              >
                {s}
              </span>
            ))}
            {product.sizes.length > 5 && (
              <span className="text-[9px] text-stone-400 self-center">+{product.sizes.length - 5}</span>
            )}
          </div>
        )}

        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-[11px] sm:text-sm font-semibold text-stone-900">€{product.price.toLocaleString("it-IT")}</span>
          {product.originalPrice && (
            <span className="text-[10px] sm:text-xs text-stone-400 line-through">€{product.originalPrice.toLocaleString("it-IT")}</span>
          )}
        </div>
        {product.originalPrice && (
          <p className="hidden sm:block text-[10px] text-green-700 font-medium mt-0.5">
            Risparmi €{(product.originalPrice - product.price).toLocaleString("it-IT")}
          </p>
        )}
      </div>
    </NavLink>
  );
}
